import {
    Directive,
    Input,
    OnInit,
    OnDestroy,
    TemplateRef,
    ViewContainerRef,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { PermissionService } from '../services/permission.service';

/**
 * Structural directive that conditionally renders its host element based on
 * whether the current user has any of the specified permissions.
 *
 * The directive is REACTIVE — it re-evaluates when the PermissionService's
 * `loaded$` stream emits, which happens after the async API call in
 * APP_INITIALIZER completes.  This prevents the "no menu items" bug that
 * occurs when the directive renders before permissions have been fetched.
 *
 * Usage:
 *   <!-- Single permission -->
 *   <li *appHasPermission="'CCAdmin_Permissions'">Admin Users</li>
 *
 *   <!-- Multiple permissions (OR logic — shown if user has ANY) -->
 *   <button *appHasPermission="['Marketing_View', 'Marketing_Manage']">Marketing</button>
 *
 * The element is REMOVED from the DOM (not just hidden) when the user lacks
 * the permission — safer and cleaner than CSS visibility tricks.
 */
@Directive({
    selector: '[appHasPermission]',
    standalone: true,
})
export class HasPermissionDirective implements OnInit, OnDestroy {
    @Input() appHasPermission!: string | string[];

    private sub?: Subscription;
    private hasView = false;

    constructor(
        private tpl: TemplateRef<any>,
        private vcr: ViewContainerRef,
        private permissionService: PermissionService
    ) { }

    ngOnInit(): void {
        // Apply immediately with whatever is currently in the permission set
        // (may be empty if permissions haven't loaded yet).
        this.updateView();

        // Re-apply whenever the loaded$ flag changes.  This fires once after the
        // APP_INITIALIZER resolves, causing all directives to re-render with the
        // real permissions.
        this.sub = this.permissionService.loaded$.subscribe(() => {
            this.updateView();
        });
    }

    ngOnDestroy(): void {
        this.sub?.unsubscribe();
    }

    private updateView(): void {
        const perms = Array.isArray(this.appHasPermission)
            ? this.appHasPermission
            : [this.appHasPermission];

        const allowed = this.permissionService.hasAny(...perms);

        if (allowed && !this.hasView) {
            this.vcr.createEmbeddedView(this.tpl);
            this.hasView = true;
        } else if (!allowed && this.hasView) {
            this.vcr.clear();
            this.hasView = false;
        }
    }
}
